/**
 * Survey PDF Generation Service
 * 
 * Generates PDF documents from completed survey questionnaires for HCP dashboard.
 * Uses survey-pdf@2.0.8 compatible with existing survey-core@2.0.8 setup.
 */

import { DocController, SurveyPDF } from 'survey-pdf';
import { slk } from 'survey-core';
import { roboto } from './fonts';

export interface ISurveyPdfOptions {
    /** PDF page orientation */
    orientation?: 'portrait' | 'landscape';
    /** PDF page format */
    format?: string | [number, number];
    /** Font size for PDF content */
    fontSize?: number;
    /** Font family for better character support */
    fontName?: string;
    /** PDF margins */
    margins?: {
        top?: number;
        bottom?: number;
        left?: number;
        right?: number;
    };
    /** Enable compression (disables form fields) */
    compress?: boolean;
    /** Use custom font in HTML */
    useCustomFontInHtml?: boolean;
    applyImageFit?: boolean;
}

export interface ISurveyDataForPdf {
    /** Survey JSON schema from database */
    questionnaireJson: string;
    /** User answers JSON from database */
    answersJson: string | null;
    /** Current UI language */
    locale?: string;
}

/**
 * Default PDF generation options optimized for read-only medical questionnaires
 * Uses Roboto font family with bold support for better French accent rendering
 */
const DEFAULT_PDF_OPTIONS: ISurveyPdfOptions = {
    orientation: 'portrait',
    format: 'letter',
    fontSize: 12,
    margins: {
        top: 20,
        bottom: 20,
        left: 15,
        right: 15,
    },
    compress: false, // Better readability in read-only mode
    fontName: 'Roboto', // Supports bold variants
    useCustomFontInHtml: true,
    applyImageFit: true,
};

// Embed custom fonts for four font variations
DocController.addFont('Roboto', roboto.thin, 'normal');
DocController.addFont('Roboto', roboto.medium, 'bold');
DocController.addFont('Roboto', roboto.lightitalic, 'italic');
DocController.addFont('Roboto', roboto.bolditalic, 'bolditalic');

slk(
    'ODBlNDU2NTUtZjY2Ny00MmVkLTllMjAtMjMyOGM1OWQ0NTg0OzE9MjAyNi0wOS0wOCwyPTIwMjYtMDktMDgsND0yMDI2LTA5LTA4'
);

/**
 * SurveyJS PDF Generation Service
 * 
 * Creates read-only PDF documents from completed survey responses
 * for Healthcare Professional dashboard viewing and archival.
 * 
 * Uses read-only mode to display formatted answer text (not interactive forms)
 * with bold styling for better readability.
 */
export class SurveyPdfService {
    /**
     * Generate PDF from survey data
     * 
     * @param surveyData Survey schema and answers from database
     * @param options PDF generation options
     * @returns Promise<Blob> PDF as blob for download
     */
    static async generatePdf(
        filename: string,
        surveyData: ISurveyDataForPdf,
        options: ISurveyPdfOptions = {}
    ): Promise<void> {
        try {
            // Validate input data
            if (!surveyData.questionnaireJson) 
                throw new Error('Questionnaire JSON is required');

            // Merge options with defaults
            const pdfOptions = { ...DEFAULT_PDF_OPTIONS, ...options };
            
            // Convert to survey-pdf format with proper encoding for French characters
            const pdfDocOptions = {
                format: pdfOptions.format,
                orientation: (pdfOptions.orientation === 'landscape' ? 'l' : 'p') as 'l' | 'p',
                fontSize: pdfOptions.fontSize,
                margins: pdfOptions.margins,
                compress: pdfOptions.compress,
                fontName: pdfOptions.fontName || 'Roboto',
                // Ensure proper encoding for Unicode characters
                putOnlyUsedFonts: true,
                htmlRenderAs: 'auto' as any,
                // Force consistent Unicode encoding
                useCustomFontInHtml: true,
                applyImageFit: false,
                // Enhanced readability and textbox appearance options
                hideEmptyElements: false, // Show all elements even if empty
                addEmptyPages: false,
                questionTitlePattern: 'numTitle', // Better question title formatting
                showQuestionNumbers: 'off', // Clean appearance without question numbers
            } as any; // Using 'as any' to allow for additional properties that might not be in types

            // Create SurveyPDF instance with modified JSON
            const surveyPdf = new SurveyPDF(JSON.parse(surveyData.questionnaireJson), pdfDocOptions);

            // Enable read-only mode for completed surveys (this is key for text formatting!)
            surveyPdf.readOnly = true;
            
            // Set language if provided
            if (surveyData.locale) 
                surveyPdf.locale = surveyData.locale;

            // Populate with user answers if available (clean text encoding)
            if (surveyData.answersJson) {
                try {
                    const cleanAnswers = JSON.parse(surveyData.answersJson);
                    surveyPdf.data = cleanAnswers;
                } catch (error) {
                    console.warn('Failed to parse answers JSON, generating empty PDF:', error);
                }
            }

            // Generate PDF as raw bytes
            surveyPdf.save(filename);
        } catch (error: any) {
            console.error('Error generating survey PDF:', error);
            throw new Error(`PDF generation failed: ${error?.message || 'Unknown error'}`);
        }
    }

    /**
     * Generate and download PDF directly
     * 
     * @param surveyData Survey schema and answers from database
     * @param filename Optional filename for download
     * @param options PDF generation options
     * @param viewMode 'download' | 'view' | 'both' - how to present the PDF
     */
    static async downloadPdf(
        surveyData: ISurveyDataForPdf,
        filename: string = 'survey-result.pdf',
        options: ISurveyPdfOptions = {}
    ): Promise<void> {
        try {
            // Generate PDF blob
            this.generatePdf(filename, surveyData, options);
            
        } catch (error) {
            console.error('Error downloading survey PDF:', error);
            throw error;
        }
    }

    /**
     * Generate filename for survey PDF based on task and date
     * 
     * @param taskId Task ID if available
     * @param surveyTitle Survey title if available
     * @param date Date to include in filename
     * @returns Formatted filename
     */
    static generateFilename(
        taskId?: string | number,
        surveyTitle?: string,
        date: Date = new Date()
    ): string {
        const dateString = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        // Sanitize title for filename
        const sanitizedTitle = surveyTitle
            ? surveyTitle.replace(/[^a-zA-Z0-9\-_\s]/g, '').replace(/\s+/g, '-').toLowerCase()
            : 'survey';
            
        // Build filename
        const parts = ['questionnaire', sanitizedTitle];
        if (taskId) 
            parts.push(`task-${taskId}`);
        
        parts.push(dateString);
        
        return `${parts.join('-')}.pdf`;
    }

    /**
     * Validate survey data before PDF generation
     * 
     * @param surveyData Survey data to validate
     * @returns True if valid, throws error if invalid
     */
    static validateSurveyData(surveyData: ISurveyDataForPdf): boolean {
        if (!surveyData) 
            throw new Error('Survey data is required');

        if (!surveyData.questionnaireJson || typeof surveyData.questionnaireJson !== 'string') 
            throw new Error('Valid questionnaire JSON is required');

        try {
            JSON.parse(surveyData.questionnaireJson);
        } catch {
            throw new Error('Questionnaire JSON is invalid');
        }

        if (surveyData.answersJson) {
            try {
                JSON.parse(surveyData.answersJson);
            } catch {
                throw new Error('Answers JSON is invalid');
            }
        }

        return true;
    }
}

/**
 * Export default instance for convenience
 */
export default SurveyPdfService;
